import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const ACADEMIC_YEARS = ['2025-2026', '2026-2027', '2027-2028'];

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [filterYear, setFilterYear] = useState('2026-2027');

  const [formData, setFormData] = useState({
    class_id: '',
    subject_id: '',
    teacher_id: '',
    coefficient: 1,
    hours_per_week: '',
    is_active: true,
    academic_year: '2026-2027',
  });

  // ============ TOAST ============
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // ============ CHARGEMENT ============
  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    await Promise.all([fetchCourses(), fetchClasses(), fetchSubjects(), fetchTeachers()]);
  };

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/assignments', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourses(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur cours:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/classes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur classes:', e);
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/subjects', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur subjects:', e);
    }
  };

  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/teachers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeachers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur profs:', e);
    }
  };

  // ============ FILTRES ============
  const filteredCourses = useMemo(() => {
    return courses.filter((c) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const subject = (c.subject_name || '').toLowerCase();
        const cls = (c.class_name || '').toLowerCase();
        const teacher = (c.teacher_name || '').toLowerCase();
        if (!subject.includes(term) && !cls.includes(term) && !teacher.includes(term)) {
          return false;
        }
      }
      if (filterClass !== 'all' && String(c.class_id) !== String(filterClass)) return false;
      if (filterYear && c.academic_year !== filterYear) return false;
      return true;
    });
  }, [courses, searchTerm, filterClass, filterYear]);

  // ============ STATS ============
  const stats = useMemo(() => {
    const total = filteredCourses.length;
    const active = filteredCourses.filter((c) => c.is_active !== false).length;
    const totalCoef = filteredCourses.reduce((sum, c) => sum + (parseInt(c.coefficient) || 1), 0);
    return { total, active, totalCoef };
  }, [filteredCourses]);

  // ============ CRUD ============
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        class_id: formData.class_id,
        subject_id: formData.subject_id,
        teacher_id: formData.teacher_id || null,
        coefficient: parseInt(formData.coefficient) || 1,
        hours_per_week: formData.hours_per_week ? parseFloat(formData.hours_per_week) : null,
        is_active: formData.is_active,
        academic_year: formData.academic_year,
      };

      if (editingCourse) {
        await api.put(`/assignments/${editingCourse.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showToast('Cours modifié avec succès !', 'success');
      } else {
        await api.post('/assignments', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showToast('Cours créé avec succès !', 'success');
      }
      closeModal();
      fetchCourses();
    } catch (error) {
      console.error('Erreur:', error);
      const msg =
        error.response?.data?.error ||
        (typeof error.response?.data?.errors === 'object'
          ? Object.values(error.response.data.errors).flat().join(', ')
          : "Erreur lors de l'enregistrement");
      showToast(msg, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce cours ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/assignments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Cours supprimé avec succès !', 'success');
      fetchCourses();
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      class_id: course.class_id || '',
      subject_id: course.subject_id || '',
      teacher_id: course.teacher_id || '',
      coefficient: course.coefficient || 1,
      hours_per_week: course.hours_per_week || '',
      is_active: course.is_active !== false,
      academic_year: course.academic_year || '2026-2027',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCourse(null);
    setFormData({
      class_id: '',
      subject_id: '',
      teacher_id: '',
      coefficient: 1,
      hours_per_week: '',
      is_active: true,
      academic_year: '2026-2027',
    });
  };

  const handleNew = () => {
    setEditingCourse(null);
    setFormData({
      class_id: '',
      subject_id: '',
      teacher_id: '',
      coefficient: 1,
      hours_per_week: '',
      is_active: true,
      academic_year: '2026-2027',
    });
    setShowModal(true);
  };

  return (
    <div className="p-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[280px]`}
        >
          <span className="text-xl">{toast.type === 'success' ? '✅' : '❌'}</span>
          <span className="flex-1 font-medium text-sm">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="text-white text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">🎓 Gestion des cours</h1>
          <p className="text-gray-500">
            Un cours = Matière + Classe + Enseignant
          </p>
        </div>
        <button
          onClick={handleNew}
          className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition font-medium"
        >
          ➕ Créer un cours
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 mb-1">🎓 Cours</p>
          <p className="text-2xl font-bold text-[#2C3E50]">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
          <p className="text-xs text-gray-500 mb-1">✅ Actifs</p>
          <p className="text-2xl font-bold text-green-600">{stats.active}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-orange-500">
          <p className="text-xs text-gray-500 mb-1">📊 Total coefficients</p>
          <p className="text-2xl font-bold text-orange-600">{stats.totalCoef}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="🔍 Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        />
        <select
          value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        >
          <option value="all">Toutes les classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        >
          {ACADEMIC_YEARS.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div className="text-sm text-gray-500 flex items-center px-2">
          {filteredCourses.length} cours affiché(s)
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">Chargement...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Matière</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Classe</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Enseignant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Coef</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Volume</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCourses.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">
                      {courses.length === 0
                        ? 'Aucun cours. Commencez par en créer un.'
                        : 'Aucun résultat'}
                    </td>
                  </tr>
                ) : (
                  filteredCourses.map((c) => (
                    <tr key={c.id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-[#2C3E50]">
                        📖 {c.subject_name}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {c.class_name}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {c.teacher_name || <span className="text-gray-400">Non assigné</span>}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-orange-100 text-[#E67E22] rounded-full text-xs font-medium">
                          {c.coefficient}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {c.hours_per_week ? `${c.hours_per_week}h/sem` : '-'}
                      </td>
                      <td className="py-3 px-4">
                        {c.is_active !== false ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            ● Actif
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            ○ Inactif
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleEdit(c)}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========== MODAL ========== */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-[#2C3E50] mb-4">
              {editingCourse ? '✏️ Modifier le cours' : '➕ Créer un cours'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Année scolaire *
                </label>
                <select
                  value={formData.academic_year}
                  onChange={(e) =>
                    setFormData({ ...formData, academic_year: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                >
                  {ACADEMIC_YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Matière *
                </label>
                <select
                  value={formData.subject_id}
                  onChange={(e) =>
                    setFormData({ ...formData, subject_id: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                >
                  <option value="">-- Choisir une matière --</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {subjects.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Aucune matière. <a href="/admin/subjects" className="underline">Créer des matières</a>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Classe *
                </label>
                <select
                  value={formData.class_id}
                  onChange={(e) =>
                    setFormData({ ...formData, class_id: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                >
                  <option value="">-- Choisir une classe --</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enseignant
                </label>
                <select
                  value={formData.teacher_id}
                  onChange={(e) =>
                    setFormData({ ...formData, teacher_id: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                >
                  <option value="">-- Aucun --</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.speciality ? ` (${t.speciality})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coefficient
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.coefficient}
                    onChange={(e) =>
                      setFormData({ ...formData, coefficient: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Volume horaire (h/sem)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="40"
                    placeholder="4"
                    value={formData.hours_per_week}
                    onChange={(e) =>
                      setFormData({ ...formData, hours_per_week: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.is_active === true}
                      onChange={() => setFormData({ ...formData, is_active: true })}
                      className="accent-[#E67E22]"
                    />
                    <span className="text-sm">● Actif</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.is_active === false}
                      onChange={() => setFormData({ ...formData, is_active: false })}
                      className="accent-[#E67E22]"
                    />
                    <span className="text-sm">○ Inactif</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] font-medium"
                >
                  {editingCourse ? '💾 Modifier' : '✅ Créer le cours'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCourses;